import type { ExplicitType } from "../../../utils";
import { isOfType } from "../../../utils";
import type { ILinkByUid, Uids } from "../utils";

import type { IBounds } from "./bounds";

// https://citrineinformatics.github.io/taurus-documentation/specification/attribute-templates/
interface IAttributeTemplateBase<B extends IBounds = IBounds> {
  name: string;
  bounds: B;
  uids: Uids;
  description?: string;
  tags: string[];
  audit_info?: {
    created_by: string;
    created_at: number;
    updated_by?: string;
    updated_at?: number;
  };
  // data object can sometimes have dataset field, this is an id of which dataset this data object was created in
  dataset?: string;
  persisted?: boolean;
  processing?: boolean;
}

// all templates must have the same bounds as the bounds they pair with
// this allows being strict about what bounds are allowed
// but by default it is lax
export interface IConditionAttributeTemplate<
  B extends IBounds = IBounds,
> extends IAttributeTemplateBase<B> {
  type: "condition_template";
}

export interface IParameterAttributeTemplate<
  B extends IBounds = IBounds,
> extends IAttributeTemplateBase<B> {
  type: "parameter_template";
}

export interface IPropertyAttributeTemplate<
  B extends IBounds = IBounds,
> extends IAttributeTemplateBase<B> {
  type: "property_template";
}

export type IAttributeTemplate<B extends IBounds = IBounds> =
  | IConditionAttributeTemplate<B>
  | IParameterAttributeTemplate<B>
  | IPropertyAttributeTemplate<B>;
export type AttributeTemplateType = IAttributeTemplate["type"];

export const isConditionAttributeTemplate =
  isOfType<IConditionAttributeTemplate>("condition_template");
export const isParameterAttributeTemplate =
  isOfType<IParameterAttributeTemplate>("parameter_template");
export const isPropertyAttributeTemplate =
  isOfType<IPropertyAttributeTemplate>("property_template");

export const isAttributeTemplate = (
  obj?: ExplicitType<string>,
): obj is IAttributeTemplate =>
  isConditionAttributeTemplate(obj) ||
  isParameterAttributeTemplate(obj) ||
  isPropertyAttributeTemplate(obj);

// restrict the template/bounds pairs to use the same bounds type
export type IConditionTemplatePair<B extends IBounds = IBounds> = [
  IConditionAttributeTemplate<B> | ILinkByUid,
  B | undefined,
];

export type IParameterTemplatePair<B extends IBounds = IBounds> = [
  IParameterAttributeTemplate<B> | ILinkByUid,
  B | undefined,
];

export type IPropertyTemplatePair<B extends IBounds = IBounds> = [
  IPropertyAttributeTemplate<B> | ILinkByUid,
  B | undefined,
];

export type IAttributeTemplatePair<B extends IBounds = IBounds> =
  | IConditionTemplatePair<B>
  | IParameterTemplatePair<B>
  | IPropertyTemplatePair<B>;
